import React, { useState, useEffect } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserCircle,
  faCheckCircle,
  faShieldAlt,
  faKey,
  faSave,
  faSpinner,
  faUser,
  faEnvelope,
  faPhone,
  faVenusMars,
  faIdCard,
} from "@fortawesome/free-solid-svg-icons";
import { ToastContainer, toast } from "react-toastify";
import Breadcrumb from "../../components/Breadcrumb";
import PatientPortalLayout from "../../components/Patient/PatientPortalLayout";
import styles from "../../components/Patient/PatientPortal.module.css";
import "react-toastify/dist/ReactToastify.css";

const PatientProfile = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    contactNumber: "",
    gender: "Male",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem("token");

  useEffect(() => {
    const patientId = localStorage.getItem("userId");
    const fetchProfile = async () => {
      try {
        if (patientId) {
          const response = await axios.get(`/api/patient/${patientId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const p = response.data.patient || {};
          setFormData({
            firstName: p.firstName || "",
            lastName: p.lastName || "",
            email: p.email || "",
            contactNumber: p.contactNumber || "",
            gender: p.gender || "Male",
          });
        }
      } catch (error) {
        console.error("Error fetching patient profile:", error);
        toast.error("Failed to load profile records");
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveChanges = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const userId = localStorage.getItem("userId");
      const payload = { ...formData, role: "patient" };
      await axios.patch(`/api/patient/${userId}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Profile demographics saved successfully!");
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const breadcrumbs = [
    { title: "Portal Overview", link: "/patient-dashboard" },
    { title: "My Demographics", link: "/patient-profile" },
  ];

  const fullName =
    `${formData.firstName} ${formData.lastName}`.trim() || "Verified Patient";
  const getInitials = (first, last) => {
    const f = first ? first.charAt(0).toUpperCase() : "P";
    const l = last ? last.charAt(0).toUpperCase() : "";
    return `${f}${l}`;
  };

  return (
    <PatientPortalLayout
      activePage="profile"
      patientName={fullName}
      totalAppointments={0}
    >
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Breadcrumbs Row */}
      <div className={styles.breadcrumbRow}>
        <Breadcrumb items={breadcrumbs} />
      </div>

      <section
        className={styles.profileStackGrid}
        style={{ margin: "0 auto", width: "100%", maxWidth: "860px" }}
      >
        {loading ? (
          <div style={{ padding: "4rem", textAlign: "center", color: "#64748b" }}>
            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: "2rem", color: "#2d6a4f" }} />
            <p style={{ marginTop: "12px", fontWeight: 600 }}>Loading account context...</p>
          </div>
        ) : (
          <form onSubmit={handleSaveChanges} style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
            {/* ─── HERO IDENTITY BANNER CARD ──────────────────────────── */}
            <div className={styles.profileHeroCard}>
              <div className={styles.avatarCircleLarge}>
                <span>{getInitials(formData.firstName, formData.lastName)}</span>
              </div>

              <div className={styles.profileMainBox}>
                <span className={styles.profileBadgeVerified}>
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <span>Verified Clinical Account</span>
                </span>
                <h1 className={styles.profileMainName}>{fullName}</h1>
                <p className={styles.profileMainSub}>
                  {formData.email || "Primary Patient Demographics Record"}
                </p>
              </div>
            </div>

            {/* ─── CARD 1: PERSONAL DEMOGRAPHICS & CONTACT ─────────────── */}
            <div className={styles.profileSectionCard}>
              <h2 className={styles.profileCardHeading}>
                <FontAwesomeIcon icon={faIdCard} style={{ color: "#2d6a4f" }} />
                <span>Personal Demographics</span>
              </h2>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1.5rem", width: "100%" }}>
                <div className={styles.formFieldWrap}>
                  <label className={styles.formFieldLabel}>
                    <FontAwesomeIcon icon={faUser} style={{ marginRight: "6px", color: "#64748b" }} />
                    First Name
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={styles.premiumInput}
                    style={{ width: "100%", height: "48px", boxSizing: "border-box" }}
                    placeholder="First Name"
                    required
                  />
                </div>

                <div className={styles.formFieldWrap}>
                  <label className={styles.formFieldLabel}>
                    <FontAwesomeIcon icon={faUser} style={{ marginRight: "6px", color: "#64748b" }} />
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={styles.premiumInput}
                    style={{ width: "100%", height: "48px", boxSizing: "border-box" }}
                    placeholder="Last Name"
                    required
                  />
                </div>

                <div className={styles.formFieldWrap}>
                  <label className={styles.formFieldLabel}>
                    <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: "6px", color: "#64748b" }} />
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={styles.premiumInput}
                    style={{ width: "100%", height: "48px", boxSizing: "border-box" }}
                    placeholder="patient@example.com"
                    required
                  />
                </div>

                <div className={styles.formFieldWrap}>
                  <label className={styles.formFieldLabel}>
                    <FontAwesomeIcon icon={faPhone} style={{ marginRight: "6px", color: "#64748b" }} />
                    Contact Number
                  </label>
                  <input
                    type="text"
                    name="contactNumber"
                    value={formData.contactNumber}
                    onChange={handleChange}
                    className={styles.premiumInput}
                    style={{ width: "100%", height: "48px", boxSizing: "border-box" }}
                    placeholder="+91 9876543210"
                    required
                  />
                </div>

                <div className={styles.formFieldWrap} style={{ gridColumn: "1 / -1" }}>
                  <label className={styles.formFieldLabel}>
                    <FontAwesomeIcon icon={faVenusMars} style={{ marginRight: "6px", color: "#64748b" }} />
                    Gender
                  </label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className={styles.premiumInput}
                    style={{ width: "100%", height: "48px", boxSizing: "border-box" }}
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Non-binary / Other</option>
                    <option value="Prefer not to say">Prefer not to say</option>
                  </select>
                </div>
              </div>

              {/* Action Handling: Bottom Right Save CTA */}
              <div className={styles.formSaveRow}>
                <button
                  type="submit"
                  disabled={saving}
                  className={styles.saveChangesBtn}
                >
                  <FontAwesomeIcon icon={saving ? faSpinner : faSave} spin={saving} />
                  <span>{saving ? "Saving Changes..." : "Save Demographics"}</span>
                </button>
              </div>
            </div>
          </form>
        )}
      </section>
    </PatientPortalLayout>
  );
};

export default PatientProfile;
